import { Worker } from '../lib/api'

interface WorkerGridProps {
  workers: Worker[]
}

const statusColors = {
  idle: 'bg-green-100 text-green-800',
  processing: 'bg-blue-100 text-blue-800',
  offline: 'bg-gray-100 text-gray-800',
}

export default function WorkerGrid({ workers }: WorkerGridProps) {
  if (workers.length === 0) {
    return (
      <div className="text-center py-12 bg-white rounded-lg shadow">
        <p className="text-gray-500">No workers found</p>
      </div>
    )
  }

  return (
    <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
      {workers.map((worker) => (
        <div
          key={worker.id}
          className="bg-white rounded-lg shadow p-4 hover:shadow-md transition-shadow"
        >
          <div className="flex justify-between items-start mb-3">
            <div>
              <h3 className="font-medium text-gray-900 truncate" title={worker.id}>
                {worker.id.substring(0, 20)}...
              </h3>
              <p className="text-sm text-gray-500">{worker.location}</p>
            </div>
            <span
              className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${
                statusColors[worker.status]
              }`}
            >
              {worker.status}
            </span>
          </div>

          {worker.status === 'processing' && worker.currentJob && (
            <div className="mb-3 p-2 bg-blue-50 rounded text-sm">
              <p className="font-medium text-blue-700">Current Job</p>
              <p className="text-blue-600 truncate">{worker.currentJob.jobId}</p>
              <p className="text-blue-500">{worker.currentJob.progress}</p>
              {worker.currentJob.apiKeyIndex !== undefined && worker.currentJob.apiKeyIndex > 0 && (
                <p className="text-blue-400 text-xs mt-1">API Key: #{worker.currentJob.apiKeyIndex}</p>
              )}
              {worker.currentJob.videoResolution && (
                <div className="text-blue-400 text-xs mt-1 flex flex-wrap gap-2">
                  <span>{worker.currentJob.videoResolution}</span>
                  {worker.currentJob.videoBitrateKbps !== undefined && worker.currentJob.videoBitrateKbps > 0 && (
                    <span>{worker.currentJob.videoBitrateKbps} kbps</span>
                  )}
                  {worker.currentJob.fileSizeMB !== undefined && worker.currentJob.fileSizeMB > 0 && (
                    <span>{worker.currentJob.fileSizeMB.toFixed(1)} MB</span>
                  )}
                </div>
              )}
            </div>
          )}

          <div className="flex justify-between text-sm text-gray-500">
            <span>Jobs: {worker.jobsTotal}</span>
            <span>Errors: {worker.jobsErrors}</span>
            {worker.concurrency > 0 && <span>Concurrency: {worker.concurrency}</span>}
          </div>

          <div className="mt-2 text-xs text-gray-400">
            Last heartbeat: {new Date(worker.lastHeartbeat).toLocaleTimeString()}
          </div>
        </div>
      ))}
    </div>
  )
}
